import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LockClosedIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED } from '../../../shared/config/featureFlags';
import { settingsSliceAPI } from '../store/settingsSlice';
import { getUsage } from '../services/tierApi';

/** API / storage may send booleans as strings; avoid treating "false" as truthy. */
function rewardFlag(value) {
    if (value === true || value === 1) {
        return true;
    }
    if (typeof value === 'string') {
        const s = value.trim().toLowerCase();
        return s === 'true' || s === '1';
    }
    return false;
}

export default function RewardsSettings() {
    const dispatch = useAppDispatch();
    const rewardsettings = useAppSelector((state) => state.settings.rewardsettings);
    const loading = useAppSelector((state) => state.settings.loading);
    const error = useAppSelector((state) => state.settings.error);

    const [form, setForm] = useState({
        visits_enabled: false,
        punch_cards_enabled: false,
        points_enabled: false,
        point_per_dollar: 1,
    });
    const [success, setSuccess] = useState(false);
    const [merchantTier, setMerchantTier] = useState(null);

    useEffect(() => {
        if (!rewardsettings || rewardsettings.point_per_dollar === undefined) {
            dispatch(settingsSliceAPI.fetchRewardSettings());
        }
    }, [dispatch, rewardsettings]); // Only run on mount

    useEffect(() => {
        let cancelled = false;
        getUsage()
            .then((res) => {
                if (!cancelled) {
                    const t = (res?.data?.tier ?? '').toString().toLowerCase();
                    setMerchantTier(t || null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMerchantTier(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (rewardsettings !== undefined) {
            setForm({
                visits_enabled: rewardFlag(rewardsettings.visits_enabled),
                punch_cards_enabled: rewardFlag(rewardsettings.punch_cards_enabled),
                points_enabled: rewardFlag(rewardsettings.points_enabled),
                point_per_dollar: rewardsettings.point_per_dollar ?? 1,
            });
        }
    }, [rewardsettings]);

    const isFreePlan = merchantTier === 'free';

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccess(false);
        try {
            await dispatch(
                settingsSliceAPI.updateRewardSettings({
                    visits_enabled: VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED
                        ? isFreePlan
                            ? false
                            : form.visits_enabled
                        : rewardFlag(rewardsettings?.visits_enabled),
                    punch_cards_enabled: VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED
                        ? isFreePlan
                            ? false
                            : form.punch_cards_enabled
                        : rewardFlag(rewardsettings?.punch_cards_enabled),
                    points_enabled: form.points_enabled,
                    point_per_dollar: parseFloat(form.point_per_dollar) || 0,
                })
            ).unwrap();
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            // error is handled by redux
        }
    };

    const visitPunchLockedByPlanOrFeature =
        !VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED || isFreePlan;
    const visitCheckboxChecked =
        VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED &&
        !isFreePlan &&
        form.visits_enabled;
    const punchCheckboxChecked =
        VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED &&
        !isFreePlan &&
        form.punch_cards_enabled;
    const visitPunchFieldDisabled = loading || visitPunchLockedByPlanOrFeature;

    return (
        <div className="p-6 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-primary mb-4">Rewards Settings</h2>
            <p className="text-gray-600 mb-6">
                Choose which reward systems you want to activate and set the thresholds.
            </p>

            {error && (
                <p className="text-red-600 mb-2">
                    {typeof error === 'string' ? error : JSON.stringify(error)}
                </p>
            )}
            {loading && <p className="text-blue-600 mb-2">Loading...</p>}

            {!VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED && (
                <p className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <InformationCircleIcon
                        className="h-5 w-5 shrink-0 mt-0.5 text-amber-800"
                        aria-hidden
                    />
                    <span>
                        Visits, punch card, and birthday rewards are not available yet.
                    </span>
                </p>
            )}
            {isFreePlan && (
                <p className="mb-4 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950">
                    <LockClosedIcon className="h-4 w-4 shrink-0 mt-0.5 text-sky-800" aria-hidden />
                    <span>
                    Extra rewards not included in <strong>Free plan</strong>.{' '}
                    <Link to="/dashboard/settings/membership" className="font-semibold underline">
                        See Membership
                    </Link>
                    .
                    </span>
                </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label
                        className={`flex items-start gap-2 mb-2 ${
                            visitPunchFieldDisabled ? 'opacity-80' : ''
                        }`}
                    >
                        <input
                            type="checkbox"
                            name="visits_enabled"
                            checked={visitCheckboxChecked}
                            onChange={handleChange}
                            className="mt-1 shrink-0"
                            disabled={visitPunchFieldDisabled}
                        />
                        <span>
                            <span className="flex items-center gap-2">
                                {visitPunchLockedByPlanOrFeature && (
                                    <LockClosedIcon
                                        className="h-5 w-5 shrink-0 text-gray-500"
                                        aria-hidden
                                    />
                                )}
                                <span className="block font-medium text-gray-900">Enable Visit Rewards</span>
                            </span>
                            <span className="block text-sm text-gray-600 mt-1 font-normal">
                                Customers can qualify for rewards using their visit total.
                            </span>
                        </span>
                    </label>
                </div>

                <div>
                    <label
                        className={`flex items-start gap-2 mb-2 ${
                            visitPunchFieldDisabled ? 'opacity-80' : ''
                        }`}
                    >
                        <input
                            type="checkbox"
                            name="punch_cards_enabled"
                            checked={punchCheckboxChecked}
                            onChange={handleChange}
                            className="mt-1 shrink-0"
                            disabled={visitPunchFieldDisabled}
                        />
                        <span>
                            <span className="flex items-center gap-2">
                                {visitPunchLockedByPlanOrFeature && (
                                    <LockClosedIcon
                                        className="h-5 w-5 shrink-0 text-gray-500"
                                        aria-hidden
                                    />
                                )}
                                <span className="block font-medium text-gray-900">Enable Punch Card Rewards</span>
                            </span>
                            <span className="block text-sm text-gray-600 mt-1 font-normal">
                                Stamp-style pass art and programs. Punches can follow visits, spend, or prepaid packages
                                (for example, a bought 10-pack).
                            </span>
                        </span>
                    </label>
                </div>

                <div>
                    <label className="flex items-start gap-2 mb-2 opacity-60">
                        <input
                            type="checkbox"
                            defaultChecked={false}
                            disabled
                            className="mt-1 shrink-0 cursor-not-allowed"
                            aria-disabled="true"
                        />
                        <span>
                            <span className="flex items-center gap-2">
                                <LockClosedIcon
                                    className="h-5 w-5 shrink-0 text-gray-500"
                                    aria-hidden
                                />
                                <span className="block font-medium text-gray-900">Enable Birthday Rewards</span>
                            </span>
                            <span className="block text-sm text-gray-600 mt-1 font-normal">
                                Rewards customers can qualify for based on their birthday.
                            </span>
                        </span>
                    </label>
                </div>

                <div>
                    <label className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            name="points_enabled"
                            checked={form.points_enabled}
                            onChange={handleChange}
                            className="mr-2"
                            disabled={loading}
                        />
                        Enable Point Rewards
                    </label>
                </div>

                {form.points_enabled && (
                    <div className="mt-4">
                        <label className="block mb-1 font-medium">
                            Points per Dollar Spent Ratio:
                        </label>
                        <input
                            type="number"
                            name="point_per_dollar"
                            step="0.1"
                            min="0"
                            value={form.point_per_dollar ?? ''}
                            onChange={handleChange}
                            className="border rounded p-2 w-full"
                            disabled={loading}
                        />
                    </div>
                )}

                <button
                    type="submit"
                    className="bg-primary text-white px-4 py-2 rounded hover:bg-opacity-90"
                    disabled={loading}
                >
                    Save Settings
                </button>

                {success && (
                    <p className="text-green-600 mt-2 flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden />
                        Settings saved successfully!
                    </p>
                )}
            </form>
        </div>
    );
}
